<template>
  <div class="page-view">

    <!-- Header -->
    <BasePageHeader
      :title="loading ? $t('itemMetadata.title') : (form.assetTag || form.serial_number || '#' + instanceId)"
      @back="$router.back()"
    >
      <template #actions>
        <RunMacroButton v-if="instanceId" model-type="item_metadata" :model-id="instanceId" />
        <button class="btn primary small" :disabled="saving || !formValid" @click="save">
          {{ saving ? $t('common.saving') : $t('common.save') }}
        </button>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" variant="error" :message="loadError" />

    <template v-else>

      <!-- ── Status Hero ───────────────────────────────────────────── -->
      <div class="px-4 pt-5 pb-4 border-b border-gray-800">
        <div class="flex items-center gap-3 mb-3">
          <!-- Status badge -->
          <span class="badge badge-pill text-sm px-3 py-1 font-semibold" :class="statusBadgeClass">
            <component :is="statusIcon" class="w-4 h-4 mr-1.5 inline -mt-0.5" />
            {{ statusLabel }}
          </span>
          <span v-if="form.in_downtime" class="badge badge-warning badge-pill text-xs">
            {{ $t('itemMetadata.inDowntime') }}
          </span>
        </div>

        <!-- Checked Out details -->
        <template v-if="activeCheckouts.length">
          <div
            v-for="co in activeCheckouts"
            :key="co.id"
            class="flex flex-col gap-1 px-3.5 py-3 rounded-xl border mb-2"
            :class="!co.effective_expected_checkin ? 'bg-amber-950/50 border-amber-700/50' : 'bg-[var(--color-surface)] border-gray-700'"
          >
            <div class="flex items-center gap-2 flex-wrap">
              <MdiAccountOutline class="w-4 h-4 text-gray-400 shrink-0" />
              <span class="text-sm text-gray-200 font-medium">
                {{ co.user || $t('itemMetadata.noUserAssigned') }}
              </span>
              <span v-if="co.project" class="text-xs text-gray-400">
                ·
                <MdiFolderOutline class="w-3.5 h-3.5 inline -mt-0.5 text-gray-500" />
                {{ co.project }}
              </span>
            </div>

            <div class="flex items-center gap-2 text-xs mt-0.5">
              <MdiCalendarOutline class="w-3.5 h-3.5 text-gray-500 shrink-0" />
              <span class="text-gray-400">{{ $t('itemMetadata.checkedOut', { date: fmtDatetime(co.checkout_date) }) }}</span>
            </div>

            <div class="flex items-center gap-2 text-xs mt-0.5">
              <MdiCalendarArrowRight class="w-3.5 h-3.5 shrink-0" :class="!co.effective_expected_checkin ? 'text-amber-500' : 'text-gray-500'" />
              <span v-if="co.effective_expected_checkin" class="text-gray-400">
                {{ $t('itemMetadata.expectedReturn', { date: fmtDatetime(co.effective_expected_checkin) }) }}
              </span>
              <span v-else class="text-amber-400 font-semibold">
                {{ $t('itemMetadata.noReturnDate') }}
              </span>
            </div>
          </div>
        </template>

        <div v-else-if="form.status === 1" class="text-sm text-emerald-400 flex items-center gap-2">
          <MdiCheckCircleOutline class="w-4 h-4" />
          {{ $t('itemMetadata.inServiceMsg') }}
        </div>
        <div v-else-if="form.status === 2" class="text-sm text-red-400 flex items-center gap-2">
          <MdiAlertCircleOutline class="w-4 h-4" />
          {{ $t('itemMetadata.defectiveMsg') }}
        </div>
        <div v-else-if="form.status === 3" class="text-sm text-gray-500 flex items-center gap-2">
          <MdiArchiveOutline class="w-4 h-4" />
          {{ $t('itemMetadata.retiredMsg') }}
        </div>
      </div>

      <!-- ── Availability Timeline ─────────────────────────────────── -->
      <div v-if="timelineSegments.length" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <div class="flex items-center gap-2 mb-3">
          <div class="section-label" style="margin-bottom:0">{{ $t('itemMetadata.availabilityTimeline') }}</div>
          <div class="flex items-center gap-3 ml-auto text-xs text-[var(--color-muted)]">
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-purple-500/30 border border-purple-500/40"></span> {{ $t('itemMetadata.legendPlanned') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm border border-dashed border-purple-500/60"></span> {{ $t('itemMetadata.legendTentative') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-blue-500/40 border border-blue-500/50"></span> {{ $t('itemMetadata.legendActive') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-amber-600/30 border border-amber-600/40"></span> {{ $t('itemMetadata.legendOpenEnded') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-gray-600/40 border border-gray-600/40"></span> {{ $t('itemMetadata.legendReturned') }}</span>
          </div>
        </div>
        <ItemAvailabilityTimeline :checkouts="timelineSegments" :future-days="120" />
      </div>

      <!-- ── Item type info (read-only) ───────────────────────────── -->
      <div v-if="itemInfo" class="px-4 pt-5 pb-2 border-b border-gray-800">        <div class="section-label">{{ $t('itemMetadata.instanceType') }}</div>
        <div
          class="flex items-center gap-2 px-3.5 py-2.5 bg-[var(--color-surface)] border border-gray-800 rounded-xl cursor-pointer transition-colors hover:bg-gray-800"
          @click="goToItem"
        >
          <div class="flex-1">
            <div class="text-sm font-semibold">{{ itemInfo.name }}</div>
            <div class="text-xs text-gray-500">{{ itemInfo.category?.name }}</div>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 text-gray-600 shrink-0">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </div>
      </div>

      <!-- ── Instance details ─────────────────────────────────────── -->
      <div class="px-4 pt-5 pb-2 border-b border-gray-800">
        <div class="section-label">{{ $t('itemMetadata.instanceDetails') }}</div>

        <div class="form-row">
          <BaseFormField :label="$t('itemView.assetTag')">
            <BaseInput v-model="form.assetTag" placeholder="ABC-001" maxlength="200" />
          </BaseFormField>
          <BaseFormField :label="$t('itemView.serialNumber')">
            <BaseInput v-model="form.serial_number" placeholder="SN12345" maxlength="200" />
          </BaseFormField>
        </div>

        <BaseFormField :label="$t('itemView.area')" required>
          <BaseSelect v-model="form.area">
            <option value="" disabled>{{ $t('itemView.selectArea') }}</option>
            <option v-for="a in areas" :key="a.id" :value="a.id">{{ a.name }}</option>
          </BaseSelect>
        </BaseFormField>

        <div class="form-row">
          <BaseFormField :label="$t('common.status')">
            <BaseSelect v-model="form.status">
              <option :value="1">{{ $t('instanceStatus.inService') }}</option>
              <option :value="2">{{ $t('instanceStatus.defective') }}</option>
              <option :value="3">{{ $t('instanceStatus.retired') }}</option>
            </BaseSelect>
          </BaseFormField>
          <BaseFormField v-if="isQty" :label="$t('itemView.quantity')">
            <BaseInput type="number" min="0" v-model="form.available_quantity" placeholder="0" />
          </BaseFormField>
        </div>

        <BaseFormField :label="$t('itemView.purchasePrice')">
          <div class="relative">
            <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">kr</span>
            <BaseInput style="padding-left: 1.75rem;" type="number" min="0" step="0.01" v-model="form.purchase_price" placeholder="0.00" />
          </div>
        </BaseFormField>

        <BaseFormField :label="$t('itemView.comment')">
          <BaseTextarea v-model="form.comment" :placeholder="$t('itemMetadata.commentPlaceholder')" :rows="3" />
        </BaseFormField>
      </div>

      <!-- ── Value & Depreciation ──────────────────────────────────── -->
      <div v-if="instanceId" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <div class="flex items-center gap-2 mb-3">
          <MdiTrendingDown class="w-4 h-4 text-amber-400 shrink-0" />
          <div class="section-label" style="margin-bottom:0">{{ $t('itemView.valueDepreciation') }}</div>
          <span v-if="bookValue !== null" class="ml-auto badge badge-accent badge-pill text-xs">
            {{ fmtCurrency(bookValue) }}
          </span>
        </div>

        <!-- No purchase info at all -->
        <div v-if="!hasPurchaseInfo" class="flex items-start gap-2 text-xs text-[var(--color-muted)] bg-[var(--color-surface)] rounded-xl px-3 py-2.5 border border-gray-800">
          <MdiInformation class="w-4 h-4 shrink-0 mt-0.5 text-gray-600" />
          {{ $t('itemMetadata.noPurchaseInfo') }}
        </div>

        <!-- Has purchase info but no profile -->
        <div v-else-if="!depreciationProfile" class="flex items-start gap-2 text-xs text-[var(--color-muted)] bg-[var(--color-surface)] rounded-xl px-3 py-2.5 border border-gray-800">
          <MdiInformation class="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
          {{ $t('itemMetadata.noDepProfile') }}
        </div>

        <!-- Full value card -->
        <div v-else class="flex flex-col gap-3">
          <!-- Stats row -->
          <div class="grid grid-cols-3 gap-2">
            <div class="bg-[var(--color-surface)] border border-gray-800 rounded-xl px-3 py-2.5 flex flex-col gap-0.5">
              <span class="text-[0.65rem] text-[var(--color-muted)] uppercase tracking-wide">{{ $t('itemView.statPurchase') }}</span>
              <span class="text-sm font-semibold text-gray-200">{{ fmtCurrency(purchasePrice) }}</span>
            </div>
            <div class="bg-[var(--color-surface)] border border-gray-800 rounded-xl px-3 py-2.5 flex flex-col gap-0.5">
              <span class="text-[0.65rem] text-[var(--color-muted)] uppercase tracking-wide">{{ $t('itemView.statBookValue') }}</span>
              <span class="text-sm font-semibold text-amber-300">{{ fmtCurrency(bookValue) }}</span>
            </div>
            <div class="bg-[var(--color-surface)] border border-gray-800 rounded-xl px-3 py-2.5 flex flex-col gap-0.5">
              <span class="text-[0.65rem] text-[var(--color-muted)] uppercase tracking-wide">{{ $t('itemView.statDepreciated') }}</span>
              <span class="text-sm font-semibold" :class="depreciationPct > 75 ? 'text-red-400' : depreciationPct > 40 ? 'text-orange-400' : 'text-green-400'">
                {{ depreciationPct !== null ? depreciationPct.toFixed(1) + '%' : '—' }}
              </span>
            </div>
          </div>

          <!-- Profile info -->
          <div class="flex items-center gap-2 text-xs text-[var(--color-muted)] flex-wrap">
            <span class="badge badge-muted badge-pill">{{ profileLevelLabel }}</span>
            <span class="font-medium text-gray-300">{{ depreciationProfile.name }}</span>
            <span class="text-gray-600">·</span>
            <span>{{ METHOD_LABELS[depreciationProfile.method] ?? depreciationProfile.method }}</span>
            <span class="text-gray-600">·</span>
            <span>{{ (parseFloat(depreciationProfile.annual_rate) * 100).toFixed(1) }}{{ $t('ui.pA') }}</span>
          </div>

          <!-- Depreciation progress bar -->
          <div class="relative h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              class="absolute left-0 top-0 h-full rounded-full transition-all"
              :class="depreciationPct > 75 ? 'bg-red-500' : depreciationPct > 40 ? 'bg-orange-400' : 'bg-amber-400'"
              :style="{ width: (depreciationPct ?? 0) + '%' }"
            />
          </div>

          <!-- Schedule toggle -->
          <button
            class="flex items-center gap-1.5 text-xs text-[var(--color-muted)] hover:text-gray-300 transition-colors cursor-pointer bg-transparent border-none p-0 self-start"
            @click="showDepSchedule = !showDepSchedule"
          >
            <component :is="showDepSchedule ? MdiChevronUp : MdiChevronDown" class="w-4 h-4" />
            {{ showDepSchedule ? $t('itemView.hideSchedule') : $t('itemView.showSchedule') }}
          </button>

          <!-- Schedule table -->
          <div v-if="showDepSchedule" class="overflow-x-auto">
            <table class="w-full text-xs text-left border-collapse">
              <thead>
                <tr class="text-[var(--color-muted)] border-b border-gray-800">
                  <th class="py-1.5 pr-4 font-medium">{{ $t('itemView.tableYear') }}</th>
                  <th class="py-1.5 pr-4 font-medium text-right">{{ $t('itemView.tableValueStart') }}</th>
                  <th class="py-1.5 font-medium text-right">{{ $t('itemView.tableDepreciation') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in depSchedule"
                  :key="row.year"
                  class="border-b border-gray-900"
                  :class="row.isCurrent ? 'bg-amber-950/30 text-amber-200' : 'text-gray-400'"
                >
                  <td class="py-1.5 pr-4 font-mono">
                    {{ row.year }}
                    <span v-if="row.isCurrent" class="ml-1.5 badge badge-warning badge-pill text-[0.6rem]">{{ $t('itemView.nowBadge') }}</span>
                  </td>
                  <td class="py-1.5 pr-4 text-right font-mono">{{ fmtCurrency(row.value) }}</td>
                  <td class="py-1.5 text-right font-mono text-red-400/80">-{{ fmtCurrency(row.depreciation) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ── Contained in (parent link) ────────────────────────────── -->
      <div v-if="parentInstance" class="px-4 pt-5 pb-2 border-b border-gray-800">
        <div class="section-label">{{ $t('itemMetadata.containedIn') }}</div>
        <div
          class="flex items-center gap-2 px-3.5 py-2.5 bg-[var(--color-surface)] border border-gray-800 rounded-xl cursor-pointer transition-colors hover:bg-gray-800"
          @click="goToParent"
        >
          <MdiPackageVariantClosed class="w-4 h-4 text-teal-400 shrink-0" />
          <div class="flex-1">
            <div class="text-sm font-semibold">{{ parentInstance.item?.name ?? $t('itemMetadata.container') }}</div>
            <div class="text-xs text-gray-500">
              {{ parentInstance.assetTag || parentInstance.serial_number || '#' + parentInstance.id }}
            </div>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 text-gray-600 shrink-0">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </div>
      </div>

      <!-- ── Contained Items (container instances) ────────────────── -->
      <ContainedItemsSection
        v-if="itemInfo?.is_container"
        :parent-id="Number(instanceId)"
      />

      <!-- ── Downtime ─────────────────────────────────────────────── -->
      <div class="px-4 pt-5 pb-2 border-b border-gray-800">
        <div class="section-label">{{ $t('itemMetadata.downtime') }}</div>

        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-300">{{ $t('itemMetadata.inDowntime') }}</span>
          <button
            class="relative w-11 h-6 rounded-full border-none cursor-pointer transition-colors shrink-0"
            :class="form.in_downtime ? 'bg-blue-600' : 'bg-gray-700'"
            @click="form.in_downtime = !form.in_downtime"
          >
            <span
              class="absolute top-[3px] left-[3px] w-[18px] h-[18px] rounded-full bg-white transition-transform"
              :class="form.in_downtime ? 'translate-x-5' : 'translate-x-0'"
            />
          </button>
        </div>

        <BaseFormField v-if="form.in_downtime" :label="$t('itemMetadata.downtimeReason')" class="mt-3">
          <BaseInput v-model="form.downtime_reason" :placeholder="$t('itemMetadata.describeIssue')" />
        </BaseFormField>
        <div class="flex items-center gap-3 mt-3.5 flex-wrap">
          <button
            v-if="!form.in_downtime"
            class="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold cursor-pointer border-none bg-amber-900 text-amber-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-800"
            :disabled="togglingDowntime"
            @click="markDowntimeReason = ''; showMarkDowntimeModal = true"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
            {{ $t('itemView.markMaintenanceBtn') }}
          </button>
          <button
            v-else
            class="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold cursor-pointer border-none bg-green-900 text-green-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-800"
            :disabled="togglingDowntime"
            @click="showReturnServiceModal = true"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
            {{ $t('itemView.returnToServiceBtn') }}
          </button>
          <router-link
            :to="`/maintenance/schedules?instance=${instanceId}`"
            class="text-xs text-blue-400 no-underline whitespace-nowrap hover:underline"
          >{{ $t('itemView.viewSchedulesLink') }}</router-link>
          <button
            class="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold cursor-pointer border-none bg-blue-900 text-blue-300 transition-colors hover:bg-blue-800"
            @click="openAddScheduleModal"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="14" x2="12" y2="18"/><line x1="10" y1="16" x2="14" y2="16"/></svg>
            {{ $t('itemView.addToSchedule') }}
          </button>
        </div>
      </div>

      <!-- ── Upcoming Project Plans ────────────────────────────────── -->
      <div v-if="projectPlans.length || plansLoading || plansTotalCount > 0" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <div class="flex items-center gap-2 mb-3">
          <div class="section-label" style="margin-bottom:0">{{ $t('itemMetadata.upcomingPlans') }}</div>
          <span v-if="plansTotalCount > plansPageSize" class="text-xs text-[var(--color-muted)] ml-auto">
            {{ $t('itemMetadata.totalPlans', { count: plansTotalCount }) }}
          </span>
        </div>

        <div v-if="plansLoading" class="state-empty text-sm py-3">{{ $t('common.loading') }}</div>
        <div v-else-if="!projectPlans.length" class="state-empty text-sm py-2">{{ $t('itemMetadata.noUpcomingPlans') }}</div>
        <ul v-else class="flex flex-col gap-2">
          <li
            v-for="plan in projectPlans"
            :key="plan.id"
            class="flex items-center gap-3 px-3.5 py-2.5 bg-[var(--color-surface)] border border-gray-800 rounded-xl"
          >
            <MdiFolderOutline class="w-4 h-4 text-blue-400 shrink-0" />
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-200 truncate">
                {{ plan.project ? (projectNameMap[plan.project] ?? $t('common.view') + ' ' + plan.project) : $t('common.unknown') }}
              </div>
              <div class="text-xs text-gray-500">
                {{ plan.item_name ?? '' }}
                <span v-if="plan.item_name"> · </span>
                {{ $t('itemMetadata.qtyLabel') }} {{ plan.quantity ?? 1 }}
                <span v-if="plan.checkout_status && plan.checkout_status !== 'none'" class="ml-2 badge badge-muted text-[0.65rem]">{{ plan.checkout_status }}</span>
              </div>
            </div>
            <router-link
              v-if="plan.project"
              :to="`/project/${plan.project}/items`"
              class="text-xs text-blue-400 hover:underline no-underline whitespace-nowrap"
            >{{ $t('common.view') }} →</router-link>
          </li>
        </ul>

        <!-- Pagination -->
        <div v-if="plansTotalPages > 1" class="flex items-center justify-center gap-3 mt-3 text-sm">
          <button
            class="btn ghost small"
            :disabled="plansPage === 1 || plansLoading"
            @click="loadPlans(plansPage - 1)"
          >← {{ $t('common.prev') }}</button>
          <span class="text-[var(--color-muted)] text-xs">{{ plansPage }} / {{ plansTotalPages }}</span>
          <button
            class="btn ghost small"
            :disabled="plansPage >= plansTotalPages || plansLoading"
            @click="loadPlans(plansPage + 1)"
          >{{ $t('common.next') }} →</button>
        </div>
      </div>

      <!-- ── Checkout History ──────────────────────────────────────── -->
      <div v-if="checkoutHistory.length" class="px-4 pt-5 pb-2 border-b border-gray-800">
        <div class="section-label">{{ $t('itemMetadata.checkoutHistory') }}</div>
        <ul class="flex flex-col gap-2">
          <li
            v-for="co in checkoutHistory"
            :key="co.id"
            class="flex items-start gap-3 px-3.5 py-2.5 bg-[var(--color-surface)] border border-gray-800 rounded-xl"
          >
            <div
              class="w-2 h-2 rounded-full mt-1.5 shrink-0"
              :class="co.actual_checkin ? 'bg-gray-600' : 'bg-blue-500'"
            />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="text-sm font-medium text-gray-200">{{ co.user || co.checked_out_by || $t('common.unknown') }}</span>
                <span v-if="co.project" class="text-xs text-gray-500">·  {{ co.project }}</span>
              </div>
              <div class="text-xs text-gray-500 mt-0.5">
                <span>{{ fmtDatetime(co.checkout_date) }}</span>
                <span v-if="co.actual_checkin"> {{ $t('itemMetadata.returnedOn', { date: fmtDatetime(co.actual_checkin) }) }}</span>
                <span v-else-if="co.effective_expected_checkin" class="text-blue-400"> {{ $t('itemMetadata.expectedOn', { date: fmtDatetime(co.effective_expected_checkin) }) }}</span>
                <span v-else class="text-amber-400"> {{ $t('itemMetadata.openEnded') }}</span>
              </div>
            </div>
            <span
              v-if="!co.actual_checkin"
              class="badge badge-pill shrink-0 text-[0.65rem]"
              :class="co.effective_expected_checkin ? 'badge-accent' : 'badge-warning'"
            >{{ co.effective_expected_checkin ? $t('itemMetadata.checkoutStatusOut') : $t('itemMetadata.checkoutStatusOpen') }}</span>
            <span v-else class="badge badge-muted badge-pill shrink-0 text-[0.65rem]">{{ $t('itemMetadata.returnedBadge') }}</span>
          </li>
        </ul>
        <div v-if="historyTruncated" class="text-xs text-[var(--color-muted)] text-center pt-2 pb-1">
          {{ $t('itemMetadata.showingRecent', { count: checkoutHistory.length }) }}
        </div>
      </div>

      <!-- ── Add to project (when mode is active) ─────────────────── -->
      <div v-if="store.addToProjectMode" class="px-4 pt-5 pb-2 border-b border-purple-900 bg-[#1a0533]">
        <div class="flex items-center gap-2 text-sm text-purple-200 mb-3.5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0 text-purple-400">
            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4"/><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
          </svg>
          <span>{{ $t('items.addingTo') }} <strong class="text-purple-300">{{ store.addToProjectProject?.name }}</strong></span>
          <button
            class="ml-auto bg-transparent border border-purple-700 text-purple-400 rounded px-2 py-0.5 text-xs font-semibold cursor-pointer hover:bg-purple-900"
            @click="store.disableAddToProject()"
          >{{ $t('common.cancel') }}</button>
        </div>
        <button
          class="w-full bg-violet-600 text-white border-none rounded-xl py-3 text-base font-bold cursor-pointer transition-colors hover:bg-violet-700 disabled:opacity-45 disabled:cursor-not-allowed"
          :disabled="addingToProject"
          @click="addToProject"
        >
          {{ addingToProject ? $t('itemMetadata.addingToProject') : $t('itemMetadata.addToProject') }}
        </button>
      </div>

      <!-- ── Add to checkout ───────────────────────────────────────── -->
      <div v-if="!store.addToProjectMode" class="px-4 pt-3 pb-2">
        <button
          class="w-full bg-emerald-800 text-emerald-100 border-none rounded-xl py-3 text-base font-bold cursor-pointer transition-colors hover:bg-emerald-700"
          @click="addToCheckout"
        >
          {{ $t('itemMetadata.addToCheckout') }}
        </button>
      </div>

      <!-- ── Danger zone ────────────────────────────────────────────── -->
      <div class="px-4 py-5">
        <div class="section-label danger">{{ $t('common.dangerZone') }}</div>
        <button class="btn danger w-full" :disabled="deleting" @click="onDelete">
          {{ deleting ? $t('common.deleting') : $t('itemMetadata.deleteInstance') }}
        </button>
      </div>

    </template>

    <!-- Delete confirm modal -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="showDeleteConfirm = false">
      <div class="confirm-modal">
        <div class="confirm-modal__title">{{ $t('itemMetadata.deleteTitle') }}</div>
        <div class="confirm-modal__body">
          <span v-html="$t('itemMetadata.deleteBody', { tag: form.assetTag || form.serial_number || '#' + instanceId })"></span>
        </div>
        <div class="confirm-modal__actions">
          <button class="confirm-btn cancel" @click="showDeleteConfirm = false">{{ $t('common.cancel') }}</button>
          <button class="confirm-btn delete" :disabled="deleting" @click="confirmDelete">
            {{ deleting ? $t('common.deleting') : $t('common.delete') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add to maintenance schedule modal -->
    <div v-if="showAddScheduleModal" class="modal-overlay" @click.self="closeAddScheduleModal">
      <div class="confirm-modal max-h-[85vh] overflow-y-auto">
        <div class="confirm-modal__title">{{ $t('itemMetadata.addToScheduleTitle') }}</div>
        <BaseFormField :label="$t('itemMetadata.addToScheduleSearchPlaceholder')">
          <BaseInput
            v-model="addScheduleSearch"
            autocomplete="off"
            :placeholder="$t('itemMetadata.addToScheduleSearchPlaceholder')"
            @input="onAddScheduleSearch"
          />
        </BaseFormField>
        <div v-if="addScheduleLoading" class="text-xs text-gray-500 text-center py-3">{{ $t('itemView.searching') }}</div>
        <div v-else-if="addScheduleResults.length === 0 && addScheduleSearch.trim()" class="text-xs text-gray-500 text-center py-3">{{ $t('itemView.noSchedules') }}</div>
        <div v-else-if="addScheduleResults.length === 0" class="text-xs text-gray-500 text-center py-3">{{ $t('itemMetadata.typeToSearch') }}</div>
        <div class="flex flex-col gap-1 mb-1">
          <button
            v-for="s in addScheduleResults"
            :key="s.id"
            class="flex items-center gap-2 w-full bg-[var(--color-surface)] border border-gray-700 rounded-lg px-3 py-2.5 cursor-pointer text-left transition-colors hover:bg-gray-800 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="addingScheduleId === s.id"
            @click="addToSchedule(s)"
          >
            <div class="flex-1 text-sm font-semibold text-gray-100">{{ s.title }}</div>
            <div class="flex gap-1.5 items-center">
              <span v-if="s.next_due_date" class="text-xs text-gray-400">{{ $t('itemMetadata.scheduleDue', { date: fmtDate(s.next_due_date) }) }}</span>
              <span v-if="s.frequency" class="text-[0.7rem] text-gray-500 bg-slate-900 px-1 rounded">{{ s.frequency }}</span>
            </div>
            <span v-if="addingScheduleId === s.id" class="text-xs text-blue-400 whitespace-nowrap">{{ $t('itemView.adding') }}</span>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-4 h-4 text-gray-600 shrink-0">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </button>
        </div>
        <div class="confirm-modal__actions" style="margin-top:.75rem">
          <button class="confirm-btn cancel" @click="closeAddScheduleModal">{{ $t('common.close') }}</button>
        </div>
      </div>
    </div>

    <!-- Mark maintenance needed modal -->
    <div v-if="showMarkDowntimeModal" class="modal-overlay" @click.self="showMarkDowntimeModal = false">
      <div class="confirm-modal">
        <div class="confirm-modal__title">{{ $t('itemMetadata.markMaintenanceTitle') }}</div>
        <div class="confirm-modal__body">{{ $t('itemMetadata.markMaintenanceBody') }}</div>
        <BaseFormField :label="$t('itemMetadata.downtimeReason')" class="mb-4">
          <BaseInput v-model="markDowntimeReason" :placeholder="$t('itemMetadata.describeIssue')" @keydown.enter="confirmMarkMaintenance" />
        </BaseFormField>
        <div class="confirm-modal__actions">
          <button class="confirm-btn cancel" @click="showMarkDowntimeModal = false">{{ $t('common.cancel') }}</button>
          <button class="confirm-btn" style="background:#d97706;color:#fff" :disabled="togglingDowntime" @click="confirmMarkMaintenance">
            {{ togglingDowntime ? $t('common.working') : $t('itemMetadata.markMaintenanceConfirmBtn') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Return to service modal -->
    <div v-if="showReturnServiceModal" class="modal-overlay" @click.self="showReturnServiceModal = false">
      <div class="confirm-modal">
        <div class="confirm-modal__title">{{ $t('itemMetadata.returnToServiceTitle') }}</div>
        <div class="confirm-modal__body">{{ $t('itemMetadata.returnToServiceBody') }}</div>
        <div class="confirm-modal__actions">
          <button class="confirm-btn cancel" @click="showReturnServiceModal = false">{{ $t('common.cancel') }}</button>
          <button class="confirm-btn" style="background:#16a34a;color:#fff" :disabled="togglingDowntime" @click="confirmReturnService">
            {{ togglingDowntime ? $t('common.working') : $t('itemView.returnToServiceBtn') }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/stores/store'
import { useToast } from '@/composables/useToast'
import {
  getItemInstance, updateItemInstance, deleteItemInstance, getItemInstances,
  getAreas, addItemToProjectById, patchItemMetadataDowntime,
  getMaintenanceSchedules, updateMaintenanceSchedule,
  getActiveCheckouts, getCheckoutHistoryForInstance, getProjectPlansForInstance,
  getProject,
} from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import RunMacroButton from '@/components/macros/RunMacroButton.vue'
import ItemAvailabilityTimeline from '@/components/ItemAvailabilityTimeline.vue'
import { getItemMetadataTimeline } from '@/api/api.js'
import ContainedItemsSection from '@/components/ContainedItemsSection.vue'
import MdiAccountOutline from '~icons/mdi/account-outline'
import MdiFolderOutline from '~icons/mdi/folder-outline'
import MdiCalendarOutline from '~icons/mdi/calendar-outline'
import MdiCalendarArrowRight from '~icons/mdi/calendar-arrow-right'
import MdiCheckCircleOutline from '~icons/mdi/check-circle-outline'
import MdiAlertCircleOutline from '~icons/mdi/alert-circle-outline'
import MdiArchiveOutline from '~icons/mdi/archive-outline'
import MdiSwapHorizontal from '~icons/mdi/swap-horizontal'
import MdiTrendingDown from '~icons/mdi/trending-down'
import MdiChevronDown from '~icons/mdi/chevron-down'
import MdiChevronUp from '~icons/mdi/chevron-up'
import MdiInformation from '~icons/mdi/information-outline'
import MdiPackageVariantClosed from '~icons/mdi/package-variant-closed'

const router = useRouter()
const route  = useRoute()
const store  = useStore()
const toast  = useToast()
const { t }  = useI18n()

const instanceId = computed(() => route.params.instanceId)

const form = ref({
  assetTag:           '',
  serial_number:      '',
  area:               '',
  status:             1,
  comment:            '',
  purchase_price:     '',
  available_quantity: '',
  in_downtime:        false,
  downtime_reason:    '',
})

const formValid  = computed(() => form.value.area !== '')
const loading    = ref(false)
const loadError  = ref(null)
const saving     = ref(false)
const deleting   = ref(false)
const showDeleteConfirm = ref(false)
const areas           = ref([])
const itemInfo        = ref(null)
const isQty           = ref(false)
const instanceCount   = ref(null)
const isCheckedOut    = ref(false)

// ─── operational status data ──────────────────────────────────────────────────
const activeCheckouts  = ref([])
const checkoutHistory  = ref([])
const timelineSegments = ref([])
const historyTruncated = ref(false)
const projectPlans     = ref([])
const plansPage        = ref(1)
const plansPageSize    = 5
const plansTotalCount  = ref(0)
const plansLoading     = ref(false)
const projectNameMap   = ref({})
const plansTotalPages  = computed(() => Math.ceil(plansTotalCount.value / plansPageSize))

// Full API response stored for depreciation section
const instanceData = ref(null)
const showDepSchedule = ref(false)
const parentInstance = ref(null)

// ─── Depreciation / Value helpers ─────────────────────────────────────────────

function calcBookValue(purchasePrice, purchaseDate, profile, asOfDate) {
  if (!purchasePrice || !purchaseDate || !profile) return null
  const pp = parseFloat(purchasePrice)
  const salvage = pp * parseFloat(profile.salvage_value_percentage ?? 0)
  const msPurchase = new Date(purchaseDate).getTime()
  const msNow = asOfDate ? new Date(asOfDate).getTime() : Date.now()
  const yearsElapsed = (msNow - msPurchase) / (365.25 * 24 * 3600 * 1000)

  if (profile.method === 'STRAIGHT_LINE') {
    return Math.max(pp - pp * parseFloat(profile.annual_rate) * yearsElapsed, salvage)
  }
  if (profile.method === 'DECLINING_BALANCE') {
    return Math.max(pp * Math.pow(1 - parseFloat(profile.annual_rate), yearsElapsed), salvage)
  }
  if (profile.method === 'INSURANCE_STEP') {
    const grace = parseFloat(profile.grace_period_years ?? 0)
    if (yearsElapsed <= grace) return pp
    const depYears = yearsElapsed - grace
    return Math.max(pp - pp * parseFloat(profile.annual_rate) * depYears, salvage)
  }
  return null
}

const depreciationProfile = computed(() => instanceData.value?.effective_depreciation_profile ?? null)
const purchasePrice       = computed(() => parseFloat(instanceData.value?.purchase_price) || null)
const purchaseDate        = computed(() => instanceData.value?.purchase_date ?? null)
const bookValue           = computed(() => {
  const bv = instanceData.value?.book_value
  return bv !== null && bv !== undefined ? parseFloat(bv) : null
})

const depreciationPct = computed(() => {
  if (purchasePrice.value && bookValue.value !== null) {
    return Math.max(0, Math.min(100, (1 - bookValue.value / purchasePrice.value) * 100))
  }
  return null
})

const profileLevelLabel = computed(() => {
  if (!depreciationProfile.value) return null
  const instProfileId = instanceData.value?.depreciation_profile
  const itemProfileId = instanceData.value?.item?.depreciation_profile
  if (instProfileId) return t('itemView.instanceOverride')
  if (itemProfileId) return t('itemView.itemDefault')
  return t('itemView.tenantDefault')
})

const METHOD_LABELS = computed(() => ({
  STRAIGHT_LINE: t('itemView.methodStraightLineDisplay'),
  DECLINING_BALANCE: t('itemView.methodDecliningBalanceDisplay'),
  INSURANCE_STEP: t('itemView.methodInsuranceStepDisplay'),
}))

const depSchedule = computed(() => {
  const pp = purchasePrice.value
  const pd = purchaseDate.value
  const profile = depreciationProfile.value
  if (!pp || !pd || !profile) return []

  const purchaseYear = new Date(pd).getFullYear()
  const currentYear  = new Date().getFullYear()
  const lifeYears    = Math.ceil(1 / parseFloat(profile.annual_rate || 1)) + 2
  const endYear      = Math.max(currentYear + 3, purchaseYear + lifeYears)

  const rows = []
  for (let yr = purchaseYear; yr <= endYear; yr++) {
    const atStart = calcBookValue(pp, pd, profile, `${yr}-01-01`)
    const atEnd   = calcBookValue(pp, pd, profile, `${yr}-12-31`)
    if (atStart === null) break
    rows.push({
      year: yr,
      value: atStart,
      depreciation: atStart - (atEnd ?? atStart),
      isCurrent: yr === currentYear,
    })
    if (atEnd !== null && atEnd <= pp * parseFloat(profile.salvage_value_percentage ?? 0) + 0.01) break
  }
  return rows
})

const hasValueData = computed(() => !!(purchasePrice.value && purchaseDate.value && depreciationProfile.value))
const hasPurchaseInfo = computed(() => !!(purchasePrice.value && purchaseDate.value))

function fmtCurrency(val) {
  if (val === null || val === undefined) return '—'
  return new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', maximumFractionDigits: 0 }).format(val)
}

const STATUS_LABELS = computed(() => ({
  1: t('instanceStatus.inService'),
  2: t('instanceStatus.defective'),
  3: t('instanceStatus.retired'),
}))
const statusLabel   = computed(() => {
  if (isCheckedOut.value && form.value.status === 1) return t('checkout.checkedOutBadge')
  return STATUS_LABELS.value[form.value.status] ?? t('itemTypes.unknown')
})

const statusBadgeClass = computed(() => {
  if (isCheckedOut.value && form.value.status === 1) return 'badge-warning'
  if (form.value.status === 1) return 'badge-success'
  if (form.value.status === 2) return 'badge-danger'
  if (form.value.status === 3) return 'badge-muted'
  return 'badge-muted'
})

const statusIcon = computed(() => {
  if (isCheckedOut.value && form.value.status === 1) return MdiSwapHorizontal
  if (form.value.status === 1) return MdiCheckCircleOutline
  if (form.value.status === 2) return MdiAlertCircleOutline
  if (form.value.status === 3) return MdiArchiveOutline
  return MdiArchiveOutline
})

async function load() {
  loading.value   = true
  loadError.value = null
  try {
    const areaParams = store.currentWarehouse?.id ? { warehouse: store.currentWarehouse.id } : {}
    const [inst, areaRes] = await Promise.all([
      getItemInstance(instanceId.value),
      getAreas(areaParams),
    ])
    areas.value = areaRes.results ?? areaRes ?? []

    form.value.assetTag           = inst.assetTag          ?? ''
    form.value.serial_number      = inst.serial_number     ?? ''
    form.value.area               = typeof inst.area === 'object' ? inst.area?.id : inst.area
    form.value.status             = inst.status            ?? 1
    form.value.comment            = inst.comment           ?? ''
    form.value.purchase_price     = inst.purchase_price    ?? ''
    form.value.available_quantity = inst.available_quantity ?? ''
    form.value.in_downtime        = inst.in_downtime       ?? false
    form.value.downtime_reason    = inst.downtime_reason   ?? ''

    itemInfo.value    = inst.item ?? null
    instanceData.value = inst
    isQty.value       = inst.item?.item_type === 3
    isCheckedOut.value = inst.is_checked_out ?? false

    // Load parent container info if this instance belongs to one
    const parentId = typeof inst.parent === 'object' ? inst.parent?.id : inst.parent
    if (parentId) {
      getItemInstance(parentId).then(p => { parentInstance.value = p }).catch(() => {})
    } else {
      parentInstance.value = null
    }

    if (inst.item?.id) {
      const instList = await getItemInstances(inst.item.id)
      instanceCount.value = instList.count ?? (instList.results ?? instList)?.length ?? null
    }

    // Load operational context in parallel (non-blocking failures)
    const [checkoutsRes, historyRes, timelineRes] = await Promise.allSettled([
      getActiveCheckouts({ item_metadata: instanceId.value }),
      getCheckoutHistoryForInstance(instanceId.value, 10),
      getItemMetadataTimeline(instanceId.value),
    ])

    if (checkoutsRes.status === 'fulfilled') {
      activeCheckouts.value = checkoutsRes.value?.results ?? checkoutsRes.value ?? []
    }
    if (historyRes.status === 'fulfilled') {
      const all = historyRes.value?.results ?? historyRes.value ?? []
      const PAGE_SIZE = 10
      historyTruncated.value = (historyRes.value?.count ?? all.length) > PAGE_SIZE
      checkoutHistory.value  = all
    }
    if (timelineRes.status === 'fulfilled') {
      timelineSegments.value = timelineRes.value?.segments ?? []
    }
    // Plans load independently so pagination controls work from the start
    loadPlans(1)
  } catch {
    loadError.value = t('itemMetadata.loadFailed')
  } finally {
    loading.value = false
  }
}

function goToItem() {
  if (itemInfo.value?.id) router.push(`/item/${itemInfo.value.id}`)
}

function goToParent() {
  if (!parentInstance.value) return
  const p = parentInstance.value
  if (p.item?.item_type === 2) {
    router.push(`/instance/${p.id}`)
  } else if (p.item?.id) {
    router.push(`/item/${p.item.id}`)
  }
}

async function loadPlans(page = 1) {
  plansLoading.value = true
  plansPage.value    = page
  try {
    const res = await getProjectPlansForInstance(instanceId.value, { page, pageSize: plansPageSize })
    const allPlans = res.results ?? res ?? []
    plansTotalCount.value = res.count ?? allPlans.length
    const filtered = allPlans.filter(p => !p.removed_at)
    projectPlans.value = filtered

    // Resolve project names for any IDs not yet in the cache
    const unknownIds = [...new Set(filtered.map(p => p.project).filter(id => id && !projectNameMap.value[id]))]
    if (unknownIds.length) {
      const results = await Promise.allSettled(unknownIds.map(id => getProject(id)))
      unknownIds.forEach((id, i) => {
        if (results[i].status === 'fulfilled') {
          projectNameMap.value[id] = results[i].value?.name ?? t('ui.projectNumber', { id: id })
        }
      })
    }
  } catch {
    // non-critical; plans section stays hidden
  } finally {
    plansLoading.value = false
  }
}

async function save() {
  if (!formValid.value || saving.value) return
  saving.value = true
  try {
    const payload = {
      area:          parseInt(form.value.area, 10),
      status:        form.value.status,
      in_downtime:   form.value.in_downtime,
      downtime_reason: form.value.downtime_reason,
    }
    if (form.value.assetTag)              payload.assetTag          = form.value.assetTag
    if (form.value.serial_number)         payload.serial_number     = form.value.serial_number
    if (form.value.comment)               payload.comment           = form.value.comment
    if (form.value.purchase_price !== '') payload.purchase_price    = parseFloat(form.value.purchase_price)
    if (isQty.value && form.value.available_quantity !== '')
      payload.available_quantity = parseInt(form.value.available_quantity, 10)

    await updateItemInstance(instanceId.value, payload)
    toast.success(t('itemMetadata.saved'))
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('itemMetadata.saveFailed'))
  } finally {
    saving.value = false
  }
}

function onDelete() {
  if (instanceCount.value === 1) {
    toast.error(t('itemMetadata.lastInstanceError'))
    return
  }
  showDeleteConfirm.value = true
}

async function confirmDelete() {
  deleting.value = true
  try {
    await deleteItemInstance(instanceId.value)
    toast.success(t('itemMetadata.deleted'))
    router.back()
  } catch {
    toast.error(t('itemMetadata.deleteFailed'))
    deleting.value = false
    showDeleteConfirm.value = false
  }
}

onMounted(load)

// ─── quick maintenance actions ───────────────────────────────────────────────
const showMarkDowntimeModal = ref(false)
const markDowntimeReason    = ref('')
const showReturnServiceModal = ref(false)
const togglingDowntime      = ref(false)

async function confirmMarkMaintenance() {
  togglingDowntime.value = true
  try {
    await patchItemMetadataDowntime(instanceId.value, true, markDowntimeReason.value.trim())
    form.value.in_downtime     = true
    form.value.downtime_reason = markDowntimeReason.value.trim()
    toast.success(t('itemMetadata.markedDowntime'))
    showMarkDowntimeModal.value = false
    markDowntimeReason.value    = ''
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('itemMetadata.downtimeFailed'))
  } finally {
    togglingDowntime.value = false
  }
}

async function confirmReturnService() {
  togglingDowntime.value = true
  try {
    await patchItemMetadataDowntime(instanceId.value, false, '')
    form.value.in_downtime     = false
    form.value.downtime_reason = ''
    toast.success(t('itemMetadata.returnedToService'))
    showReturnServiceModal.value = false
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('itemMetadata.downtimeFailed'))
  } finally {
    togglingDowntime.value = false
  }
}
// ─── add to maintenance schedule ─────────────────────────────────────────────
const showAddScheduleModal = ref(false)
const addScheduleSearch    = ref('')
const addScheduleResults   = ref([])
const addScheduleLoading   = ref(false)
const addingScheduleId     = ref(null)
let   addScheduleTimer     = null

function openAddScheduleModal() {
  addScheduleSearch.value  = ''
  addScheduleResults.value = []
  showAddScheduleModal.value = true
}

function closeAddScheduleModal() {
  showAddScheduleModal.value = false
}

function onAddScheduleSearch() {
  clearTimeout(addScheduleTimer)
  if (!addScheduleSearch.value.trim()) {
    addScheduleResults.value = []
    return
  }
  addScheduleLoading.value = true
  addScheduleTimer = setTimeout(async () => {
    try {
      const res = await getMaintenanceSchedules({ search: addScheduleSearch.value.trim(), page_size: 15 })
      addScheduleResults.value = res.results ?? res ?? []
    } catch {
      addScheduleResults.value = []
    } finally {
      addScheduleLoading.value = false
    }
  }, 300)
}

async function addToSchedule(schedule) {
  if (addingScheduleId.value) return
  addingScheduleId.value = schedule.id
  try {
    const existingIds = (schedule.items ?? []).map(i => i.id ?? i)
    const newId = parseInt(instanceId.value, 10)
    if (existingIds.includes(newId)) {
      toast.info(t('itemMetadata.alreadyOnSchedule', { title: schedule.title }))
      return
    }
    await updateMaintenanceSchedule(schedule.id, { items: [...existingIds, newId] })
    toast.success(t('itemMetadata.addedToScheduleToast', { title: schedule.title }))
    closeAddScheduleModal()
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('itemMetadata.addToScheduleFailed'))
  } finally {
    addingScheduleId.value = null
  }
}

function fmtDate(d) {
  if (!d) return ''
  try { return new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return d }
}

function fmtDatetime(d) {
  if (!d) return ''
  try {
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch { return d }
}

const addingToProject = ref(false)

async function addToProject() {
  if (addingToProject.value) return
  addingToProject.value = true
  try {
    const sid = store.addToProjectProject?.section_id ?? null
    const res = await addItemToProjectById(store.addToProjectProject.id, instanceId.value, { section_id: sid })
    toast.success(t('itemMetadata.addedToProject', { name: store.addToProjectProject.name }))
    if (res?.conflicts?.length) {
      toast.warning(t('itemMetadata.conflictProject', { names: res.conflicts.map(c => c.project_name).join(', ') }))
    }
  } catch (err) {
    toast.error(err?.data?.error ?? err?.message ?? t('itemMetadata.addToProjectFailed'))
  } finally {
    addingToProject.value = false
  }
}

function addToCheckout() {
  const id = instanceId.value
  const name = instanceData.value?.item?.name ?? form.value.assetTag ?? `#${id}`
  if ((store.checkoutItemList ?? []).some(e => !e._isBundle && String(e.id) === String(id))) {
    toast.info(t('itemMetadata.alreadyInCheckout', { name }))
    return
  }
  store.addCheckoutItem({
    id,
    item: instanceData.value?.item ?? { name },
    serial_number: form.value.serial_number,
  })
  toast.success(t('itemMetadata.addedToCheckout', { name }))
}
</script>

